// src/components/AIToolsPage.js
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import ReactMarkdown from 'react-markdown';
import Select, { components } from 'react-select';
import remarkGfm from 'remark-gfm';
import { FaTrash } from 'react-icons/fa';
import { getAuth } from 'firebase/auth';
import { useData } from '../context/DataContext';
import {
  fetchLLMOptions as apiFetchLLMOptions,
  fetchChatHistory as apiFetchChatHistory,
  createChat as apiCreateChat,
  sendMessage as apiSendMessage,
  setChatTitle as apiSetChatTitle,
  deleteChat as apiDeleteChat,
  AI_TOOLS_ERROR_CODES,
} from '../api/AiToolsApi';
import styles from './AIToolsPage.module.css';
import Spinner from './Spinner';

// --- NEW: Helper to grab initial checkbox state without flicker ---
const getInitialCheckboxes = () => {
  try {
    // Prefer the active session storage, fallback to the legacy local storage
    const stored =
      sessionStorage.getItem('aiToolsCheckboxes') ||
      localStorage.getItem('knowledgeCheckBoxes');
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.warn('Failed to parse knowledge checks from storage:', e);
  }
  return {
    basic: false,
    products: false,
    videos: false,
    emails: false,
    deepDive: false,
  };
};

const AIToolsPage = () => {
  // Get global state/functions from Context
  const {
    activeinfluencerId,
    aiChats,
    addChat,
    deleteChatById,
    updateChatTitle,
    queryResults,
    setError: setGlobalError,
  } = useData();

  const auth = getAuth();

  // --- Local State ---
  const [currentChat, setCurrentChat] = useState([]);
  const [currentChatId, setCurrentChatId] = useState(null);
  // *** Persisted Prompt ***
  const [prompt, setPrompt] = useState(() => {
    return sessionStorage.getItem('aiToolsDraftPrompt') || '';
  });

  // LLM state
  const [availableLLMs, setAvailableLLMs] = useState([]);
  const [selectedLLMId, setSelectedLLMId] = useState(null);
  const [defaultLLMId, setDefaultLLMId] = useState(null);
  const [currentTokenCount, setCurrentTokenCount] = useState(0);
  const [currentTokenLimit, setCurrentTokenLimit] = useState(0);
  const [knowledgeCutoffDate, setKnowledgeCutoffDate] = useState(null);
  const [lastChatDate, setLastChatDate] = useState(null);

  const [selectedInfluencers, setSelectedInfluencers] = useState(() => {
    try {
      const stored = sessionStorage.getItem('aiToolsSelectedInfluencers');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  // *** Persisted Knowledge Checkboxes (No Flicker) ***
  const initialChecks = getInitialCheckboxes();
  const [knowledgeBasic, setKnowledgeBasic] = useState(initialChecks.basic);
  const [knowledgeProducts, setKnowledgeProducts] = useState(
    initialChecks.products,
  );
  const [knowledgeVideos, setKnowledgeVideos] = useState(initialChecks.videos);
  const [knowledgeEmails, setKnowledgeEmails] = useState(initialChecks.emails);
  const [knowledgeDeepDive, setKnowledgeDeepDive] = useState(
    initialChecks.deepDive,
  );

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingChat, setIsLoadingChat] = useState(false);
  const [error, setError] = useState(null);

  // Thought bubble state
  const [thoughtPhase, setThoughtPhase] = useState(null);
  const [thoughtSnippet, setThoughtSnippet] = useState('');

  // Ref for auto-scroll
  const chatEndRef = useRef(null);

  // Persisted response-time averages by LLM
  const responseTimes = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('aiResponseTimes') || '{}');
    } catch {
      return {};
    }
  }, []);

  // Multi Select components for the Influencer UI
  const influencerSelectOptions = useMemo(() => {
    return (
      (queryResults || [])
        // 1. FIX: Filter out any null, undefined, or non-object items to prevent crashes.
        .filter((inf) => typeof inf === 'object' && inf !== null)
        .map((inf) => ({
          // 2. Use optional chaining and provide a fallback for the value.
          value: String(inf?.influencerId || ''),
          // 3. The label logic is already great, but we can make it even safer.
          label:
            inf?.name ||
            inf?.nickname ||
            `ID: ${inf?.influencerId || 'Unknown'}`,
        }))
    );
  }, [queryResults]);

  const selectedInfluencerValues = useMemo(() => {
    return influencerSelectOptions.filter((option) =>
      selectedInfluencers.includes(option.value),
    );
  }, [selectedInfluencers, influencerSelectOptions]);

  const handleSelectChange = (selectedOptions) => {
    // 1. Use Array.isArray() for a robust check, providing an empty array as a fallback.
    const selectedIds = (Array.isArray(selectedOptions) ? selectedOptions : [])
      // 2. Filter out any items that are null, undefined, or not objects.
      .filter(
        (option) =>
          option && typeof option === 'object' && option.value !== undefined,
      )
      // 3. Safely map the remaining valid items to their values.
      .map((option) => option.value);

    setSelectedInfluencers(selectedIds);
  };

  // Utility for small delays
  const delay = (ms) => new Promise((res) => setTimeout(res, ms));

  // Extract a brief snippet for the "Context" bubble
  function extractContextSnippet(prompt, chatHistory) {
    const head = prompt.slice(0, 50);
    const lastMsg = chatHistory.length
      ? chatHistory[chatHistory.length - 1].content
          .split(' ')
          .slice(-10)
          .join(' ')
      : '';
    const snippet = head + (lastMsg ? ` …${lastMsg}` : '');
    return snippet.length >= 50 ? snippet.slice(0, 50) + '…' : snippet;
  }

  // Build a small summary of knowledge/configuration
  function buildKnowledgeSummary({
    knowledgeBasic,
    knowledgeProducts,
    knowledgeVideos,
    knowledgeEmails,
    knowledgeDeepDive,
    selectedInfluencers,
  }) {
    let summary = '';
    let hasContent = false;

    // Check if any flags are set
    if (
      knowledgeBasic ||
      knowledgeProducts ||
      knowledgeVideos ||
      knowledgeEmails ||
      knowledgeDeepDive
    ) {
      summary = 'Evaluating ';
      let separator = ' • '; // Separator for subsequent items

      // Process each flag
      if (knowledgeBasic) {
        summary += separator + 'profile';
        separator = ' • '; // Reset to ensure consistent spacing
        hasContent = true;
      }
      if (knowledgeProducts) {
        summary += separator + 'publications';
        separator = ' • ';
        hasContent = true;
      }
      if (knowledgeVideos) {
        summary += separator + 'relationship';
        separator = ' • ';
        hasContent = true;
      }
      if (knowledgeEmails) {
        summary += separator + 'knowledge';
        separator = ' • ';
        hasContent = true;
      }
      if (knowledgeDeepDive) {
        summary += separator + 'deepdive';
        separator = ' • ';
        hasContent = true;
      }
      // Handle selected influencers
      if (selectedInfluencers.length) {
        summary +=
          separator + ` for (${selectedInfluencers.length} influencers ...)`;
        separator = ' • ';
        hasContent = true;
      }

      // Trim leading/trailing separators if needed (unlikely here due to logic)
    }

    // Return formatted result
    return hasContent ? summary : 'No extras';
  }

  // Map phase keys to icons
  const ICON_FOR = (phase) => {
    switch (phase) {
      case 'context':
        return '💬';
      case 'knowledge':
        return '💡';
      case 'draft':
        return '✏️';
      case 'polish':
        return '🎨';
      default:
        return '';
    }
  };

  // --- Effects ---

  // Fetch LLM options on mount (Keep as is)
  useEffect(() => {
    const loadLLMOptions = async () => {
      const user = auth.currentUser;
      if (!user) return;
      setIsLoading(true); // Combine loading states later if needed
      setError(null);
      try {
        const token = await user.getIdToken(true);
        const result = await apiFetchLLMOptions(token);
        if (result && !result.error) {
          setAvailableLLMs(result.options || []);
          setDefaultLLMId(result.defaultId || null);

          // *** NEW: Restore previously selected LLM ***
          const storedLLM = sessionStorage.getItem('aiToolsSelectedLLM');
          setSelectedLLMId(
            storedLLM ? parseInt(storedLLM, 10) : result.defaultId || null,
          );
        } else {
          setError(result?.message || 'Failed to load LLM options.');
          setAvailableLLMs([]);
          setDefaultLLMId(null);
          setSelectedLLMId(null);
        }
      } catch (err) {
        setError(err || 'An unexpected error occurred loading LLMs.');
      } finally {
        setIsLoading(false);
      }
    };
    loadLLMOptions();
  }, [auth]);

  // *** REFACTORED: Persist knowledge checkboxes to Session and Local Storage ***
  useEffect(() => {
    const data = {
      basic: knowledgeBasic,
      products: knowledgeProducts,
      videos: knowledgeVideos,
      emails: knowledgeEmails,
      deepDive: knowledgeDeepDive,
    };
    try {
      sessionStorage.setItem('aiToolsCheckboxes', JSON.stringify(data));
      // Keep legacy localStorage updated so settings survive hard page reloads
      localStorage.setItem('knowledgeCheckBoxes', JSON.stringify(data));
    } catch (e) {
      console.warn('Failed to save knowledgeCheckBoxes to storage:', e);
    }
  }, [
    knowledgeBasic,
    knowledgeProducts,
    knowledgeVideos,
    knowledgeEmails,
    knowledgeDeepDive,
  ]);

  // *** NEW: Persist Prompt string to sessionStorage ***
  useEffect(() => {
    sessionStorage.setItem('aiToolsDraftPrompt', prompt);
  }, [prompt]);

  // *** NEW: Persist selected influencers to sessionStorage ***
  useEffect(() => {
    sessionStorage.setItem(
      'aiToolsSelectedInfluencers',
      JSON.stringify(selectedInfluencers),
    );
  }, [selectedInfluencers]);

  // Auto-scroll to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [currentChat]);

  // *** REFACTORED: Effect to pre-select an influencer without wiping out cache ***
  useEffect(() => {
    // 1. If the user cleared the query results, reset the selection
    if (!queryResults || queryResults.length === 0) {
      setSelectedInfluencers([]);
    }
    // 2. If there are results, but NO influencers are selected (e.g. fresh load, no cache)
    else if (selectedInfluencers.length === 0) {
      if (activeinfluencerId) {
        setSelectedInfluencers([activeinfluencerId]);
      } else {
        setSelectedInfluencers([queryResults[0].influencerId]);
      }
    }
    // 3. If selectedInfluencers already has items (e.g., restored from cache), do nothing!
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryResults]);

  // --- Handlers ---

  const handleLLMChange = (e) => {
    const id = parseInt(e.target.value, 10);
    const validId = isNaN(id) ? null : id;
    setSelectedLLMId(validId);

    // *** NEW: Persist LLM selection ***
    if (validId) {
      sessionStorage.setItem('aiToolsSelectedLLM', validId);
    }
  };

  const handleKnowledgeChange = (setter) => (e) => {
    setter(e.target.checked);
  };

  // Core function to send prompt (handles both new and existing chats)
  const handleGo = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) {
      setError('User not authenticated.');
      return;
    }
    if (!prompt.trim()) {
      setError('Please enter a prompt.');
      return;
    }

    // Specific checks for NEW chat creation
    if (!currentChatId) {
      if (!selectedLLMId) {
        setError('Please select an LLM model to start a new chat.');
        return;
      }
      // Optional: Require at least one influencer?
      if (selectedInfluencers.length === 0) {
        setError('Please select at least one influencer to start a new chat.');
        return;
      }
      if (
        !knowledgeBasic &&
        !knowledgeProducts &&
        !knowledgeVideos &&
        !knowledgeEmails &&
        !knowledgeDeepDive
      ) {
        setError(
          'Please select at least one checkbox ( Profile, Publications, Relationship, Knowledge ) below to start a new chat.',
        );
        return;
      }
    }
    setIsLoading(true);
    setError(null);

    // Prepare timing & delays
    const start = performance.now();
    const avg = responseTimes[selectedLLMId] || 4500;
    console.log('Avg responseTimes: ', avg);
    const contextDelay = Math.min(Math.max(avg * 0.2, 100), 4500);
    const knowledgeDelay = Math.min(Math.max(avg * 0.3, 100), 6000);
    const polishDelay = 2500;
    const userContent = prompt.trim();
    setThoughtPhase('context');
    setThoughtSnippet(extractContextSnippet(userContent, currentChat));

    const currentPrompt = prompt.trim();
    const userMsg = { role: 'user', content: currentPrompt };

    // schedule Phase → Knowledge
    const kTimer = setTimeout(() => {
      setThoughtPhase('knowledge');
      setThoughtSnippet(
        buildKnowledgeSummary({
          knowledgeBasic,
          knowledgeProducts,
          knowledgeVideos,
          knowledgeEmails,
          knowledgeDeepDive,
          selectedInfluencers,
        }),
      );
    }, contextDelay);

    // schedule Phase → Draft
    const dTimer = setTimeout(() => {
      setThoughtPhase('draft');
      setThoughtSnippet('Drafting answer…');
    }, contextDelay + knowledgeDelay);

    // Optimistic UI
    setCurrentChat((prev) => [...prev, userMsg]);
    setPrompt('');

    try {
      const token = await user.getIdToken(true);
      let result;

      if (currentChatId) {
        // --- Send message to existing chat ---
        result = await apiSendMessage(token, currentChatId, currentPrompt);
        if (result && !result.error) {
          // *** Update token count on success ***
          setCurrentTokenCount(result.tokenCount ?? currentTokenCount); // Use new count, fallback to old if missing
        }
      } else {
        // --- Create a new chat ---
        const params = {
          prompt: currentPrompt,
          llmConfigId: selectedLLMId,
          selectedInfluencers: selectedInfluencers,
          knowledgeBasic: knowledgeBasic,
          knowledgeProducts: knowledgeProducts,
          knowledgeVideos: knowledgeVideos,
          knowledgeEmails: knowledgeEmails,
          knowledgeDeepDive: knowledgeDeepDive,
        };
        result = await apiCreateChat(token, params);
        if (result && !result.error) {
          // *** Update token count AND limit on success ***
          setCurrentTokenCount(result.tokenCount ?? 0);
          setCurrentTokenLimit(result.tokenLimit ?? 0);
        }
      }

      // Polishing flourish
      setThoughtPhase('polish');
      setThoughtSnippet('Putting on the finishing touches…');
      await delay(polishDelay);

      // Stop timers
      clearTimeout(kTimer);
      clearTimeout(dTimer);

      // Record timing
      const took = performance.now() - start;
      const prevAvg = responseTimes[selectedLLMId] || took;
      const newAvg = (prevAvg + took) / 2;
      localStorage.setItem(
        'aiResponseTimes',
        JSON.stringify({
          ...responseTimes,
          [selectedLLMId]: newAvg,
        }),
      );

      console.log('API Result:', result);

      // Handle API result
      if (result && !result.error) {
        // --- Success (Common logic) ---
        if (result.assistantMessage) {
          setCurrentChat((prev) => [...prev, result.assistantMessage]);
        }
        if (result.chatId && result.title) {
          // New chat specific
          setCurrentChatId(result.chatId);
          addChat({
            chatId: result.chatId,
            titleChat: result.title,
            updatedAt: new Date().toISOString(),
          });
        }
        setError(null);
      } else {
        // --- API Error (Common logic) ---
        console.error('API Error:', result);
        setError(result?.message || 'An error occurred. Please try again.');
        setCurrentChat((prev) => prev.slice(0, -1)); // Revert optimistic update
        setPrompt(currentPrompt);
        if (result?.error === AI_TOOLS_ERROR_CODES.CONTEXT_LIMIT_EXCEEDED) {
          // Optionally update token count even on limit error if backend provides it
          setCurrentTokenCount(result.data?.tokenCount ?? currentTokenCount);
          setCurrentTokenLimit(result.data?.tokenLimit ?? currentTokenLimit);
          console.warn('Context limit reached for chat:', currentChatId);
        }
      }
    } catch (err) {
      console.error('Exception in handleGo:', err);
      const formattedError = {
        code: err.code || 'AITOOLS_GO_FAILURE',
        message:
          err.message ||
          'An unexpected network or server error occurred. Please check your network connection and try again.',
      };
      setGlobalError(formattedError); // Trigger the modal
      setCurrentChat((prev) => prev.slice(0, -1));
      setPrompt(currentPrompt);
    } finally {
      setThoughtPhase(null);
      setThoughtSnippet('');
      setIsLoading(false);
      setTimeout(
        () => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }),
        100,
      );
    }
  }, [
    auth,
    prompt,
    currentChatId,
    selectedLLMId,
    selectedInfluencers,
    knowledgeBasic,
    knowledgeProducts,
    knowledgeVideos,
    knowledgeEmails,
    knowledgeDeepDive,
    addChat,
    responseTimes,
    currentChat,
    setGlobalError,
    // Add token state setters as dependencies? No, they are set within the callback.
    currentTokenCount,
    currentTokenLimit, // Add these to read fallback values if needed
  ]);

  // Load a chat from the history list
  const handleLoadChat = useCallback(
    async (chatId) => {
      // ... (initial checks: user, same chat) ...
      const user = auth.currentUser;
      if (!user) {
        setError('User not authenticated.');
        return;
      }
      if (chatId === currentChatId) return;

      setIsLoadingChat(true);
      setError(null);
      // *** Reset token counts before loading new chat ***
      setCurrentTokenCount(0);
      setCurrentTokenLimit(0);

      try {
        const token = await user.getIdToken(true);
        // *** Use the combined API function ***
        const result = await apiFetchChatHistory(token, chatId);

        if (result && !result.error) {
          console.log('AiChats raw Data', result);
          setCurrentChat(result.messages); // Load messages
          setCurrentChatId(chatId);
          // *** Set token count and limit from loaded data ***
          setCurrentTokenCount(result.currentTokenCount ?? 0);
          setCurrentTokenLimit(result.tokenLimit ?? 0);
          setKnowledgeCutoffDate(result.knowledgeCutoffDate ?? '');
          setLastChatDate(result.lastChatDate ?? '');
          setPrompt('');
          setSelectedInfluencers([]); // Reset influencer selection
          setIsLoadingChat(false);
        } else {
          const formattedError = {
            code: 'LOAD_CHAT_FAILURE',
            message:
              result?.message ||
              'Failed to load chat history. Please try again. If the error persists contact technical support.',
          };
          setGlobalError(formattedError);
          setCurrentChatId(null);
          setIsLoadingChat(false);
          // Keep token counts reset
        }
      } catch (err) {
        // ... (exception handling) ...
        const formattedError = {
          code: err.code || 'LOAD_CHAT_EXCEPTION',
          message:
            err.message ||
            'An unexpected error occurred while loading the chat. Please try again in a few minutes. If the error persists contact technical support.',
        };
        setGlobalError(formattedError);
        setCurrentChat([]);
        setCurrentChatId(null);
        setCurrentTokenCount(0);
        setCurrentTokenLimit(0);
        setIsLoadingChat(false);
      } finally {
        setIsLoadingChat(false);
      }
    },
    [auth, currentChatId, setGlobalError],
  ); // Add setters to deps? No.

  // Delete a chat
  const handleDeleteChat = useCallback(
    async (chatId) => {
      // ... (initial checks: user, confirm) ...
      const user = auth.currentUser;
      if (!user) {
        setError('User not authenticated.');
        return;
      }
      if (!window.confirm('Are you sure you want to delete this chat?')) return;

      setError(null);
      try {
        const token = await user.getIdToken(true);
        const result = await apiDeleteChat(token, chatId);

        if (result && result.success) {
          deleteChatById(chatId);
          if (chatId === currentChatId) {
            // *** Reset token counts when active chat is deleted ***
            setCurrentChat([]);
            setCurrentChatId(null);
            setPrompt('');
            setSelectedInfluencers([]);
            setCurrentTokenCount(0);
            setCurrentTokenLimit(0);
          }
        } else {
          const formattedError = {
            code: 'DELETE_CHAT_FAILURE',
            message: result?.message || 'Failed to delete chat.',
          };
          setGlobalError(formattedError);
        }
      } catch (err) {
        // ... (exception handling) ...
        const formattedError = {
          code: err.code || 'DELETE_CHAT_EXCEPTION',
          message:
            err.message ||
            'An unexpected error occurred while deleting the chat. Please try again. If the error persists contact technical support.',
        };
        setGlobalError(formattedError);
      }
    },
    [auth, currentChatId, deleteChatById, setGlobalError],
  ); // Add setters to deps? No.

  // Start a new chat session
  const handleNewChat = useCallback(() => {
    setCurrentChat([]);
    setCurrentChatId(null);
    setPrompt('');
    setError(null);
    setSelectedLLMId(defaultLLMId);
    setSelectedInfluencers([]);
    // *** Reset token counts for new chat ***
    setCurrentTokenCount(0);
    setCurrentTokenLimit(0);
  }, [defaultLLMId]); // Add setters to deps? No.

  // Set a custom title for the current chat (Keep as is)
  const handleSetCustomTitle = useCallback(async () => {
    // ... implementation unchanged ...
    const user = auth.currentUser;
    if (!user) {
      setError('User not authenticated.');
      return;
    }
    if (!currentChatId) {
      setError('No active chat selected to set title.');
      return;
    }

    const newTitle = window.prompt('Enter custom title for this chat:');
    if (newTitle && newTitle.trim()) {
      const trimmedTitle = newTitle.trim();
      setError(null);
      try {
        const token = await user.getIdToken(true);
        const result = await apiSetChatTitle(
          token,
          currentChatId,
          trimmedTitle,
        );
        if (result && !result.error) {
          updateChatTitle(currentChatId, trimmedTitle);
        } else {
          const formattedError = {
            code: 'SET_TITLE_FAILURE',
            message: result?.message || 'Failed to update chat title.',
          };
          setGlobalError(formattedError);
        }
      } catch (err) {
        const formattedError = {
          code: err.code || 'SET_TITLE_EXCEPTION',
          message:
            err.message ||
            'An unexpected error occurred updating title. Please try again.',
        };
        setGlobalError(formattedError);
      }
    } else if (newTitle !== null) {
      setError(
        'Title cannot be empty. Please enter a valid title and try again.',
      ); // Local validation error
    }
  }, [auth, currentChatId, updateChatTitle, setGlobalError]);

  // --- Calculate Token Percentage ---
  const percentageUsed = useMemo(() => {
    if (currentTokenLimit > 0 && currentTokenCount >= 0) {
      // Ensure count doesn't exceed limit visually, cap at 100%
      const calculated = (currentTokenCount / currentTokenLimit) * 100;
      return Math.min(calculated, 100);
    }
    return 0;
  }, [currentTokenCount, currentTokenLimit]);

  // Determine progress bar color based on usage
  const progressBarColorClass = useMemo(() => {
    if (percentageUsed >= 90) return styles.progressBarRed;
    if (percentageUsed >= 75) return styles.progressBarYellow;
    return styles.progressBarGreen;
  }, [percentageUsed]);

  // --- Render Logic ---

  if (!queryResults || !queryResults.length) {
    return (
      <p className="noData">
        No data available. Please run a query to select influencers to work
        with.
      </p>
    );
  }

  // *** NEW: Custom MenuList component for React-Select ***
  const MenuList = (props) => {
    const { options, selectProps } = props;
    const allSelected = selectProps.value.length === options.length;

    const handleSelectAll = () => {
      // The `onChange` prop is passed down through selectProps
      if (allSelected) {
        selectProps.onChange([]); // Deselect all
      } else {
        selectProps.onChange(options); // Select all
      }
    };

    return (
      // This is the important part: we render our custom button
      // and then render the original MenuList component from react-select
      <components.MenuList {...props}>
        <div className={styles.selectAllContainer}>
          <button
            type="button"
            className={styles.selectAllButton}
            onClick={handleSelectAll}
          >
            {allSelected ? 'Deselect All' : 'Select All'}
          </button>
        </div>
        {props.children}
      </components.MenuList>
    );
  };

  // *** NEW: Custom styles object to control the height of the selection area ***
  const customSelectStyles = {
    // Targets the container that holds the selected values
    valueContainer: (provided, state) => ({
      ...provided, // Keep the default styles
      maxHeight: '120px', // Set a maximum height (e.g., 120px allows for ~3 rows)
      overflowY: 'auto', // Make it scroll vertically when content exceeds maxHeight
      // Align items to the top instead of the default 'center' when scrolling is active
      alignItems: 'flex-start',
    }),
    // Optional: A small tweak to the input to ensure it's positioned correctly
    input: (provided, state) => ({
      ...provided,
      // Ensures the input doesn't get pushed down weirdly when there are many items
      marginTop: '2px',
    }),
  };

  return (
    <div className={styles.aiToolsPage}>
      {/* Left side: Chat history (Keep as is) */}
      {isLoadingChat && (
        <Spinner message="Please wait while loading Chat history..." />
      )}
      <div className={styles.leftContainer}>
        {/* ... chat list rendering ... */}
        <h3 className={styles.leftTitle}>Chat History</h3>
        {aiChats.length === 0 ? (
          <p className={styles.noChats}>No saved chats yet.</p>
        ) : (
          <ul className={styles.chatList}>
            {/* FIX: Add a fallback and filter to prevent crashes */}
            {(aiChats || [])
              .filter((chat) => typeof chat === 'object' && chat !== null)
              .map((chat) => (
                <li
                  key={chat.chatId}
                  className={`${styles.chatListItem} ${chat.chatId === currentChatId ? styles.activeChatItem : ''}`}
                >
                  <button
                    className={styles.loadChatButton}
                    onClick={() => handleLoadChat(chat.chatId)}
                    disabled={isLoading}
                    title={chat.titleChat}
                  >
                    {chat.titleChat || `Chat ${chat.chatId}`}
                  </button>
                  <button
                    className={styles.deleteChatButton}
                    onClick={() => handleDeleteChat(chat.chatId)}
                    disabled={isLoading}
                  >
                    <FaTrash />
                  </button>
                </li>
              ))}
          </ul>
        )}
      </div>

      {/* Right side: Current Chat */}
      <div className={styles.rightContainer}>
        {/* Loading and Error Banners (Keep as is) */}
        {error && (
          <div className={styles.errorBanner}>
            {/* This now safely handles both error objects and strings */}
            {typeof error === 'object' && error.message
              ? error.message
              : error.toString()}
            <button onClick={() => setError(null)}>X</button>
          </div>
        )}
        {currentChat.length === 0 ? (
          /* Top layout for new chat */
          <div className={styles.promptContainerTop}>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              className={styles.promptTextareaTop}
              rows={5}
              placeholder="Start a new chat. Enter your question..."
              disabled={isLoading}
            />

            <div className={styles.dropdownRowTop}>
              {/* LLM Dropdown (Keep as is) */}
              <select
                value={selectedLLMId ?? ''}
                onChange={handleLLMChange}
                className={styles.llmDropdown}
                disabled={isLoading || availableLLMs.length === 0}
                title={
                  availableLLMs.length === 0
                    ? 'Loading LLMs...'
                    : 'Select LLM Model'
                }
              >
                <option value="" disabled>
                  {availableLLMs.length === 0
                    ? 'Loading...'
                    : '-- Select LLM --'}
                </option>
                {(availableLLMs || []).map((llm) => (
                  <option key={llm.id} value={llm.id}>
                    {llm.name || 'Unnamed LLM'}
                  </option>
                ))}
              </select>

              {/* *** UPDATED: Influencer Dropdown *** */}
              <Select
                isMulti
                closeMenuOnSelect={false} // Keeps the menu open for multi-selection
                options={influencerSelectOptions}
                value={selectedInfluencerValues}
                onChange={handleSelectChange}
                isDisabled={
                  isLoading || !queryResults || queryResults.length === 0
                }
                placeholder="Search and select influencer(s)..."
                aria-label="Select Influencers"
                className="influencer-multi-select"
                classNamePrefix="select"
                components={{ MenuList }}
                styles={customSelectStyles}
              />
              {/* Knowledge Checkboxes (Keep as is) */}
              <div className={styles.knowledgeChecks}>
                <label
                  className={styles.knowledgeLabel}
                  title="Include general profile information"
                >
                  <input
                    type="checkbox"
                    checked={knowledgeBasic}
                    onChange={handleKnowledgeChange(setKnowledgeBasic)}
                    disabled={isLoading}
                  />{' '}
                  Profile
                </label>
                <label
                  className={styles.knowledgeLabel}
                  title="Include information about publications/products"
                >
                  <input
                    type="checkbox"
                    checked={knowledgeProducts}
                    onChange={handleKnowledgeChange(setKnowledgeProducts)}
                    disabled={isLoading}
                  />{' '}
                  Publications
                </label>
                {knowledgeProducts && (
                  <label
                    className={`${styles.knowledgeLabel} ${styles.deepDiveLabel}`}
                    title="Include granular, individual video summaries for a detailed analysis."
                  >
                    <input
                      type="checkbox"
                      checked={knowledgeDeepDive}
                      onChange={handleKnowledgeChange(setKnowledgeDeepDive)}
                      disabled={isLoading}
                    />{' '}
                    Deep Dive
                  </label>
                )}
                <label
                  className={styles.knowledgeLabel}
                  title="Include information about relationships/interactions"
                >
                  <input
                    type="checkbox"
                    checked={knowledgeVideos}
                    onChange={handleKnowledgeChange(setKnowledgeVideos)}
                    disabled={isLoading}
                  />{' '}
                  Relationship
                </label>
                <label
                  className={styles.knowledgeLabel}
                  title="Include general knowledge base information"
                >
                  <input
                    type="checkbox"
                    checked={knowledgeEmails}
                    onChange={handleKnowledgeChange(setKnowledgeEmails)}
                    disabled={isLoading}
                  />{' '}
                  Knowledge
                </label>
              </div>

              <button
                onClick={handleGo}
                className={styles.goButton}
                disabled={isLoading || !prompt.trim() || !selectedLLMId}
              >
                Chat!
              </button>
              <button
                onClick={handleNewChat}
                className={styles.newChatButton}
                disabled={isLoading}
              >
                New Chat
              </button>
            </div>
          </div>
        ) : (
          /* Layout for existing chat (Keep as is) */
          <>
            <div className={styles.chatContainer}>
              {(currentChat || []).map((msg, idx) => (
                <div
                  key={idx}
                  className={
                    msg.role === 'user'
                      ? styles.userMessage
                      : styles.assistantMessage
                  }
                >
                  {/*}  <span className={styles.messageRole}>{msg.role === "user" ? "You: " : "LLM: "}</span> */}
                  <div className={styles.messageContent}>
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        h1: ({ children, ...props }) => (
                          <h1
                            style={{
                              fontSize: '2em',
                              fontWeight: 800,
                              marginTop: '0.5rem',
                              marginBottom: '0rem',
                              color: '#08b3d1',
                            }}
                            {...props}
                          >
                            {children}
                          </h1>
                        ),
                        h2: ({ children, ...props }) => (
                          <h2
                            style={{
                              fontSize: '1.5em',
                              fontWeight: 600,
                              marginTop: '0.5rem',
                              marginBottom: '0rem',
                              color: '#08b3d1',
                            }}
                            {...props}
                          >
                            {children}
                          </h2>
                        ),
                        h3: ({ children, ...props }) => (
                          <h3
                            style={{
                              fontSize: '1.25em',
                              fontWeight: 500,
                              marginTop: '0.5rem',
                              marginBottom: '0rem',
                              color: '#08b3d1',
                            }}
                            {...props}
                          >
                            {children}
                          </h3>
                        ),
                        h4: ({ children, ...props }) => (
                          <h4
                            style={{
                              fontSize: '1em',
                              fontWeight: 300,
                              marginTop: '0.5rem',
                              marginBottom: '0rem',
                              color: '#ddddd',
                            }}
                            {...props}
                          >
                            {children}
                          </h4>
                        ),
                        p: ({ children, ...props }) => (
                          <p
                            style={{
                              fontSize: '1em',
                              marginTop: '5px',
                              marginBottom: '5px',
                              lineHeight: '1.5',
                              color: '#ccc',
                            }}
                            {...props}
                          >
                            {children}
                          </p>
                        ),
                        li1: ({ children, ...props }) => (
                          <li
                            style={{
                              fontSize: '1em',
                              marginLeft: '5px',
                              marginBottom: '5px',
                              color: '#d0d0d0',
                            }}
                            {...props}
                          >
                            {children}
                          </li>
                        ),
                        li2: ({ children, ...props }) => (
                          <li
                            style={{
                              fontSize: '1em',
                              marginLeft: '5px',
                              marginBottom: '5px',
                              color: '#b0b0b0',
                            }}
                            {...props}
                          >
                            {children}
                          </li>
                        ),
                        ol: ({ children, ...props }) => (
                          <ol
                            style={{
                              listStyleType: 'decimal',
                              fontSize: '1em',
                              lineHeight: '1.5',
                              paddingLeft: '1rem',
                              marginTop: '0rem',
                              marginBottom: '0rem',
                              color:
                                '#c0c0c0' /* Slightly dimmed text for lists */,
                            }}
                            {...props}
                          >
                            {children}
                          </ol>
                        ),
                        ul: ({ children, ...props }) => (
                          <ul
                            style={{
                              listStyleType: 'disc',
                              fontSize: '1em',
                              lineHeight: '1.5',
                              paddingLeft: '1rem',
                              marginTop: '0rem',
                              marginBottom: '0rem',
                              color: '#c0c0c0',
                            }}
                            {...props}
                          >
                            {children}
                          </ul>
                        ),
                      }}
                    >
                      {msg.content}
                    </ReactMarkdown>
                  </div>
                </div>
              ))}
              <div ref={chatEndRef} />
            </div>
            {/* Thought bubble under prompt */}
            {isLoading && thoughtPhase && (
              <div className={styles.thoughtBubble}>
                <span className={styles.thoughtIcon}>
                  {ICON_FOR(thoughtPhase)}
                </span>
                <span className={styles.thoughtText}>{thoughtSnippet}</span>
              </div>
            )}
            <div className={styles.promptContainerBottom}>
              {/* *** NEW: Token Usage Display *** */}
              {currentTokenLimit > 0 && (
                <div className={styles.tokenUsageDisplay}>
                  <span className={styles.tokenText}>
                    Tokens Used: {currentTokenCount} / {currentTokenLimit} (
                    {percentageUsed.toFixed(0)}%)
                  </span>
                  <progress
                    className={`${styles.tokenProgressBar} ${progressBarColorClass}`}
                    value={currentTokenCount}
                    max={currentTokenLimit}
                    aria-label={`Token usage ${percentageUsed.toFixed(0)} percent`}
                  />
                </div>
              )}

              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                className={styles.promptTextareaBottom}
                rows={2}
                placeholder="Continue your chat..."
                disabled={isLoading}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (!isLoading && prompt.trim()) {
                      handleGo();
                    }
                  }
                }}
              />
              <div className={styles.bottomRowButtons}>
                <button
                  onClick={handleGo}
                  className={styles.goButton}
                  disabled={isLoading || !prompt.trim()}
                >
                  Chat!
                </button>
                <button
                  onClick={handleSetCustomTitle}
                  className={styles.setTitleButton}
                  disabled={isLoading || !currentChatId}
                >
                  Rename Title
                </button>
                <button
                  onClick={handleNewChat}
                  className={styles.newChatButton}
                  disabled={isLoading}
                >
                  New Chat
                </button>
                <span className={styles.assessmentDate}>
                  Knowledge Cut-off:{' '}
                  {knowledgeCutoffDate
                    ? new Date(knowledgeCutoffDate).toLocaleString([], {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                      })
                    : 'N/A'}
                </span>
                <span className={styles.assessmentDate}>
                  Last Chat:{' '}
                  {lastChatDate
                    ? new Date(lastChatDate).toLocaleString([], {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                      })
                    : 'N/A'}
                </span>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AIToolsPage;
